import { useEffect, useRef, useState, useCallback } from "react";
import { Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { getUserProfile } from "../../utils/auth";
import AudioVideo from "../../images/AvatarImages/Orbiee_3_video (2).mp4";
import IdleVideo from "../../images/AvatarImages/Orbiee_3_video (2).mp4";
import { convertBuffersToWAV } from "./AudioCode";
import {
  isValidTranscription,
  isEmptyTranscriptError,
} from "../../services/websocket";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import {
  connectAvatar,
  sendText,
} from "../../services/WebRtcConnection/RealTimeAvatarAPI";
import OrbieeFlow from "./OrbieeFlow";
import {
  buildChatBubbleTextsFromResponse,
  splitAssistantPartsForDisplay,
  waitForOrbieeBackendWelcome,
  getOrbieeBackendWelcomeMessage,
} from "../../services/websocket";
import { applyWishlistFromAgentResponse } from "../../utils/applyWishlistFromAgentResponse";
import { applyCartFromAgentResponse } from "../../utils/agentCartPayload";
import {
  setVirtualTryOnHoldAvatarMic,
  isVirtualTryOnHoldingAvatarMic,
} from "../../utils/virtualTryOnAvatarHold";
import { getStoreProximitySnapshot } from "../../services/location/userLocationService";
import { buildAvatarWelcomeMessage } from "../../utils/welcomeMessage";
import {
  ANALYSER_FFT_SIZE,
  SCRIPT_PROCESSOR_BUFFER_SIZE,
  CHROMA_MIN_GREEN,
  CHROMA_DOMINANCE,
  CHROMA_SOFTNESS,
  SILENCE_THRESHOLD_START_MIC,
  SILENCE_THRESHOLD_TOGGLE_MIC,
  SPEECH_THRESHOLD_START_MIC,
  SPEECH_THRESHOLD_TOGGLE_MIC,
  SILENCE_DURATION_MS,
  STREAM_DONE_PLAYBACK_TAIL_MS,
} from "../../config/realTimeAvatarConfig";

/**
 * Display-only. Chroma key still uses each source's native videoWidth/videoHeight
 * on the canvas bitmap; this CSS stops that bitmap ratio from changing the on-screen
 * box, so idle and D-ID always paint into the same 192×30vh frame.
 */
const AVATAR_CANVAS_DISPLAY_STYLE = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  aspectRatio: "unset",
  objectFit: "fill",
};

export default function ConnectToRealTimeAvatargetVideo({
  onAvatarVideoLoadingChange,
}) {
  // ── Video refs ──────────────────────────────────────────────────────────────
  // audioVideoRef  → intro mp4 played before login
  // mainVideoRef   → single video element used for BOTH idle loop and WebRTC avatar stream
  //                  It is always mounted when step === "idle" so srcObject is never lost.
  const audioVideoRef = useRef(null);
  const mainVideoRef  = useRef(null);
  const audioCanvasRef = useRef(null);
  const mainCanvasRef  = useRef(null);
  const rafRef = useRef(null);

  // Unused loading video refs kept for potential future use
  const loadingVideoRef  = useRef(null);
  const loadingCanvasRef = useRef(null);
  const loadingRafRef    = useRef(null);

  // ── Audio / mic refs ────────────────────────────────────────────────────────
  const audioContextRef    = useRef(null);
  const analyserRef        = useRef(null);
  const processorRef       = useRef(null);
  const sourceRef          = useRef(null);
  const streamRef          = useRef(null);
  const recordedBuffersRef = useRef([]);
  const silenceTimeoutRef  = useRef(null);
  const hasSpokenRef       = useRef(false);
  const micEnabledRef      = useRef(false);
  /**
   * User ended the avatar conversation loop (stop button). Blocks automatic
   * mic reopen only — does not interrupt avatar speech already in flight.
   * Cleared when the user manually taps the mic to start listening again.
   */
  const conversationStoppedRef = useRef(false);
  const playPromiseRef     = useRef(null);
  const webrtcStreamRef    = useRef(null); // live WebRTC MediaStream
  const videoLoadedRef     = useRef(false);
  const welcomeDoneRef     = useRef(false);
  const streamDoneTimerRef = useRef(null);
  const avatarWelcomeInFlightRef = useRef(false);
  const onAvatarVideoLoadingChangeRef = useRef(onAvatarVideoLoadingChange);
  const isAvatarSpeakingRef = useRef(false);
  const vtoDeferredMicRef = useRef(false);
  const enableMicProgrammaticallyRef = useRef(null);
  const tryEnableMicAfterAvatarAndVtoRef = useRef(null);

  // ── Avatar speech queue refs ────────────────────────────────────────────────
  // speechQueueRef      → texts waiting to be spoken by the avatar (FIFO)
  // segmentInFlightRef  → a queued text has been sent and is being spoken
  // turnActiveRef       → a chat turn is still running on the backend
  // speechWatchdogRef   → failsafe timer in case stream/done never arrives
  const speechQueueRef = useRef([]);
  const segmentInFlightRef = useRef(false);
  const turnActiveRef = useRef(false);
  const speechWatchdogRef = useRef(null);
  const speakNextRef = useRef(null);
  const onAvatarSegmentEndRef = useRef(null);

  const cancelPendingStreamDone = useCallback(() => {
    if (streamDoneTimerRef.current) {
      clearTimeout(streamDoneTimerRef.current);
      streamDoneTimerRef.current = null;
    }
  }, []);

  // ── State ───────────────────────────────────────────────────────────────────
  const [step, setStep] = useState("audio"); // "audio" | "idle"
  // isAvatarSpeaking: true while the WebRTC avatar is streaming (or a video clip is playing)
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false);
  const [audioEnabled, setAudioEnabled]     = useState(false);
  const [micEnabled,   setMicEnabled]       = useState(false);
  const [isLoading,    setIsLoading]        = useState(false);
  const [isAvatarVideoLoading, setIsAvatarVideoLoading] = useState(false);

  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { addToCart, replaceCartItemsFromAgent } = useCart();
  const wishlist = useWishlist();

  const wishlistSyncRef = useRef(null);
  const cartSyncRef = useRef(null);
  useEffect(() => {
    wishlistSyncRef.current = (lastResponse) => {
      applyWishlistFromAgentResponse(lastResponse, wishlist, { navigate });
    };
  }, [wishlist, navigate]);

  useEffect(() => {
    cartSyncRef.current = (lastResponse) => {
      applyCartFromAgentResponse(replaceCartItemsFromAgent, lastResponse);
    };
  }, [replaceCartItemsFromAgent]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    onAvatarVideoLoadingChangeRef.current = onAvatarVideoLoadingChange;
  }, [onAvatarVideoLoadingChange]);

  useEffect(() => {
    isAvatarSpeakingRef.current = isAvatarSpeaking;
  }, [isAvatarSpeaking]);

  const setAvatarLoadingState = useCallback((val) => {
    setIsAvatarVideoLoading(val);
    if (onAvatarVideoLoadingChangeRef.current) {
      onAvatarVideoLoadingChangeRef.current(val);
    }
  }, []);

  const ensureAvatarConnected = useCallback(async () => {
    await connectAvatar(mainVideoRef);
  }, []);

  const resolveWelcomeMessage = useCallback((customerName) => {
    try {
      const proximity = getStoreProximitySnapshot();
      return buildAvatarWelcomeMessage(
        customerName,
        proximity?.isNearStore ? proximity.store : null
      );
    } catch (error) {
      console.warn("[location] Welcome fallback:", error);
      return buildAvatarWelcomeMessage(customerName, null);
    }
  }, []);

  /* =========================
     🎨 CHROMA KEY
  ========================= */
  const applyChromaKey = (video, canvas) => {
    if (!video || !canvas || video.readyState < 2) return;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    let frame;
    try {
      frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (e) {
      // Canvas tainted by cross-origin video — skip chroma key for this frame
      return;
    }
    const data  = frame.data;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const isGreen =
        g > CHROMA_MIN_GREEN &&
        g > r * CHROMA_DOMINANCE &&
        g > b * CHROMA_DOMINANCE;
      if (isGreen) {
        const alpha = Math.min(255, Math.max(0, (g - Math.max(r, b)) * CHROMA_SOFTNESS));
        data[i + 3] = 255 - alpha;
        data[i + 1] = Math.min(g, (r + b) / 2);
      }
    }
    ctx.putImageData(frame, 0, 0);
  };

  /* =========================
     🎞 FRAME PROCESSORS
  ========================= */
  const processFrame = useCallback(() => {
    const video  = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    const canvas = step === "audio" ? audioCanvasRef.current : mainCanvasRef.current;
    if (video && canvas && !video.paused) applyChromaKey(video, canvas);
    rafRef.current = requestAnimationFrame(processFrame);
  }, [step]);

  const processLoadingFrame = useCallback(() => {
    const video  = loadingVideoRef.current;
    const canvas = loadingCanvasRef.current;
    if (video && canvas && !video.paused) applyChromaKey(video, canvas);
    loadingRafRef.current = requestAnimationFrame(processLoadingFrame);
  }, []);

  /* =========================
     ▶️ VIDEO PLAY / PAUSE EVENTS → drive RAF
  ========================= */
  useEffect(() => {
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;

    const onPlay  = () => { cancelAnimationFrame(rafRef.current); processFrame(); };
    const onPause = () => cancelAnimationFrame(rafRef.current);

    video.addEventListener("play",  onPlay);
    video.addEventListener("pause", onPause);
    // Kick off immediately if the video is already playing (e.g. WebRTC stream arrived early)
    if (!video.paused) { cancelAnimationFrame(rafRef.current); processFrame(); }

    return () => {
      video.removeEventListener("play",  onPlay);
      video.removeEventListener("pause", onPause);
      cancelAnimationFrame(rafRef.current);
    };
  }, [step, processFrame, user, isAvatarSpeaking]);

  /* =========================
     📹 IDLE LOOP — start looping IdleVideo on mainVideoRef
  ========================= */
  const startIdleLoop = useCallback(async () => {
    const video = mainVideoRef.current;
    if (!video) return;

    // Never clobber a live avatar stream (e.g. a stale delayed timer firing
    // right after the next queued response started speaking).
    if (isAvatarSpeakingRef.current) return;

    // Cancel any in-flight play()
    if (playPromiseRef.current) {
      playPromiseRef.current.catch(() => {});
      playPromiseRef.current = null;
    }
    try { video.pause(); } catch (_) {}

    // Clear WebRTC stream if any
    if (video.srcObject) video.srcObject = null;

    video.src   = IdleVideo;
    video.loop  = true;
    video.muted = !audioEnabled;

    // Wait until the browser has enough data
    if (video.readyState < 2) {
      await new Promise((resolve) => {
        const onReady = () => { video.removeEventListener("canplay", onReady); resolve(); };
        video.addEventListener("canplay", onReady);
        setTimeout(resolve, 2000);
      });
    }

    try {
      playPromiseRef.current = video.play();
      await playPromiseRef.current;
      playPromiseRef.current = null;
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("Idle loop play error:", err);
    }
  }, [audioEnabled]);

  /* =========================
     📹 INTRO VIDEO (before login)
  ========================= */
  useEffect(() => {
    if (!user && step === "audio" && audioVideoRef.current) {
      const video  = audioVideoRef.current;
      const canvas = audioCanvasRef.current;
      video.muted = true;
      video.pause();
      const drawFirstFrame = () => {
        if (video && canvas && video.readyState >= 2) {
          video.currentTime = 0;
          setTimeout(() => { if (video && canvas && video.readyState >= 2) applyChromaKey(video, canvas); }, 100);
        }
      };
      if (video.readyState >= 2) drawFirstFrame();
      else video.addEventListener("loadeddata", drawFirstFrame, { once: true });
      return () => video.removeEventListener("loadeddata", drawFirstFrame);
    }
  }, [user, step]);

  /* =========================
     👤 USER CHECK — skip audio intro when already logged in
  ========================= */
  useEffect(() => {
    if (user && step === "audio") setStep("idle");
  }, [user, step]);

  /* =========================
     ▶️ START IDLE LOOP WHEN SWITCHING TO IDLE STATE
  ========================= */
  useEffect(() => {
    if (step === "idle" && !isAvatarSpeaking) {
      // Small delay so React has time to mount mainVideoRef
      const t = setTimeout(() => startIdleLoop(), 300);
      return () => clearTimeout(t);
    }
  }, [step, isAvatarSpeaking, startIdleLoop]);

  /* =========================
     🔊 AUDIO ENABLE / TOGGLE
  ========================= */
  const enableAudio = async () => {
    if (audioEnabled) return;
    setAudioEnabled(true);
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;
    if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
    video.muted  = false;
    video.volume = 1;
    try {
      playPromiseRef.current = video.play();
      await playPromiseRef.current;
      playPromiseRef.current = null;
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("enableAudio error:", err);
    }
  };

  const toggleAudio = async (e) => {
    e.stopPropagation();
    const video = step === "audio" ? audioVideoRef.current : mainVideoRef.current;
    if (!video) return;
    const next = !audioEnabled;
    setAudioEnabled(next);
    if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }

    // For intro video before login: wait for the video to be ready before playing
    if (step === "audio" && !user && video.readyState < 2) {
      video.load();
      await new Promise((resolve) => {
        let done = false;
        const timeout = setTimeout(() => { if (!done) { done = true; resolve(); } }, 2000);
        const onOk  = () => { if (!done) { done = true; clearTimeout(timeout); video.removeEventListener("canplay", onOk); video.removeEventListener("error", onErr); resolve(); } };
        const onErr = () => { if (!done) { done = true; clearTimeout(timeout); video.removeEventListener("canplay", onOk); video.removeEventListener("error", onErr); resolve(); } };
        video.addEventListener("canplay", onOk);
        video.addEventListener("error", onErr);
      });
    }

    video.muted  = !next;
    video.volume = 1;
    try {
      if (step === "audio" && !user && next) {
        playPromiseRef.current = video.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
      } else if (video.paused) {
        playPromiseRef.current = video.play();
        await playPromiseRef.current;
        playPromiseRef.current = null;
      }
    } catch (err) {
      playPromiseRef.current = null;
      if (err.name !== "AbortError") console.warn("toggleAudio error:", err);
    }
  };

  /* =========================
     🗣 AVATAR SPEECH QUEUE
     Each llm_response streamed over the WebSocket is queued and spoken by the
     avatar in order — starting with the FIRST response, while the backend is
     still running tools / loading products. Between queued responses (and
     whenever nothing is being spoken) the idle loop plays so the avatar is
     never frozen on a still frame. The mic is re-enabled only after the turn
     completed AND the last queued response finished playing.
  ========================= */
  const tryEnableMicAfterAvatarAndVto = useCallback(() => {
    if (conversationStoppedRef.current || micEnabledRef.current) return;

    // A chat turn is still running, or responses are still queued/speaking —
    // the mic comes back only after the LAST queued response has been spoken.
    if (
      turnActiveRef.current ||
      segmentInFlightRef.current ||
      speechQueueRef.current.length > 0
    ) {
      return;
    }

    if (isVirtualTryOnHoldingAvatarMic()) {
      vtoDeferredMicRef.current = true;
      console.debug("[VTO] deferring avatar mic — try-on image not loaded yet");
      return;
    }

    if (isAvatarSpeakingRef.current) {
      vtoDeferredMicRef.current = true;
      console.debug("[VTO] deferring avatar mic — avatar still speaking");
      return;
    }

    vtoDeferredMicRef.current = false;
    enableMicProgrammaticallyRef.current?.();
  }, []);

  const clearSpeechWatchdog = useCallback(() => {
    if (speechWatchdogRef.current) {
      clearTimeout(speechWatchdogRef.current);
      speechWatchdogRef.current = null;
    }
  }, []);

  /**
   * Switch the video element back to the looping idle clip (no mic change).
   * NOTE: Do NOT clear webrtcStreamRef here. The WebRTC peer connection and
   * its tracks remain LIVE after the avatar finishes speaking. We need the
   * stream reference to re-attach it for subsequent sendText calls, because
   * ontrack only fires once (on initial connection) — not on every message.
   */
  const goIdle = useCallback(async () => {
    setIsAvatarSpeaking(false);
    isAvatarSpeakingRef.current = false;
    welcomeDoneRef.current = true;

    const video = mainVideoRef.current;
    // Already playing the idle loop — don't restart it (avoids flicker).
    if (video && !video.srcObject && video.loop && video.src && !video.paused) {
      return;
    }

    if (video) {
      if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
      try { video.pause(); } catch (_) {}
      video.srcObject = null;
      video.src       = "";
    }

    await startIdleLoop();
  }, [startIdleLoop]);

  /** Idle loop + attempt to hand the mic back to the user. */
  const finishAvatarPlayback = useCallback(async () => {
    await goIdle();
    if (!micEnabledRef.current && !conversationStoppedRef.current) {
      tryEnableMicAfterAvatarAndVto();
    }
  }, [goIdle, tryEnableMicAfterAvatarAndVto]);

  /** Take the next queued text and have the avatar speak it. */
  const speakNextInQueue = useCallback(async () => {
    if (segmentInFlightRef.current) return;
    const text = speechQueueRef.current.shift();
    if (text == null) return;
    segmentInFlightRef.current = true;

    try {
      cancelPendingStreamDone();
      clearSpeechWatchdog();

      await ensureAvatarConnected();

      setIsAvatarSpeaking(true);
      isAvatarSpeakingRef.current = true;

      // ── CRITICAL for second+ responses ──────────────────────────────────
      // ontrack fires ONLY on the initial WebRTC connection, not on every
      // sendText call. If the video element was switched back to the idle
      // loop (src=IdleVideo), re-attach the stored live MediaStream so this
      // response is visible.
      if (webrtcStreamRef.current) {
        const vid = mainVideoRef.current;
        if (vid && vid.srcObject !== webrtcStreamRef.current) {
          if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
          vid.src       = "";   // clear idle loop source
          vid.loop      = false;
          vid.srcObject = webrtcStreamRef.current;
          vid.muted     = !audioEnabled;
          vid.volume    = 1;
          vid.play().catch((err) => { if (err.name !== "AbortError") console.warn("Re-attach play error:", err); });
        }
      }

      const videoResponse = await sendText(text);

      // Failsafe: if stream/done never arrives for this response, force the
      // queue forward so the avatar loop can never get stuck.
      const watchdogMs = Math.min(15000 + text.length * 80, 90000);
      speechWatchdogRef.current = setTimeout(() => {
        speechWatchdogRef.current = null;
        console.warn("⚠️ Avatar speech watchdog fired — forcing segment end");
        cancelPendingStreamDone();
        onAvatarSegmentEndRef.current?.();
      }, watchdogMs);

      // If the API returned a direct video URL, attach it; otherwise WebRTC handles it
      if (videoResponse) {
        const url = typeof videoResponse === "string"
          ? videoResponse
          : (videoResponse.videoUrl || videoResponse.video || videoResponse.url || null);
        if (url) {
          const video = mainVideoRef.current;
          if (video) {
            if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
            if (video.srcObject) video.srcObject = null;
            video.crossOrigin = "anonymous";
            video.src  = url;
            video.loop = false;
            video.muted = !audioEnabled;
            try {
              playPromiseRef.current = video.play();
              await playPromiseRef.current;
              playPromiseRef.current = null;
            } catch (err) {
              playPromiseRef.current = null;
              if (err.name !== "AbortError") console.warn("Video file play error:", err);
            }
          }
        }
      }
    } catch (err) {
      console.error("❌ Avatar speech error:", err);
      clearSpeechWatchdog();
      segmentInFlightRef.current = false;
      // Keep the loop alive: move on to the next queued response (or idle/mic).
      onAvatarSegmentEndRef.current?.();
    }
  }, [ensureAvatarConnected, audioEnabled, cancelPendingStreamDone, clearSpeechWatchdog]);

  /** Called when the avatar finished playing one queued response. */
  const onAvatarSegmentEnd = useCallback(async () => {
    clearSpeechWatchdog();
    segmentInFlightRef.current = false;

    // More responses queued → keep speaking.
    if (speechQueueRef.current.length > 0) {
      speakNextRef.current?.();
      return;
    }

    // Backend turn still running (products still loading) → play the idle
    // loop so the avatar is never stuck on a frozen frame; mic stays off.
    // The next llm_response will re-attach the stream and resume speaking.
    if (turnActiveRef.current) {
      await goIdle();
      return;
    }

    // Turn complete and every response spoken → idle loop + re-enable mic.
    await finishAvatarPlayback();
  }, [goIdle, finishAvatarPlayback, clearSpeechWatchdog]);

  useEffect(() => { speakNextRef.current = speakNextInQueue; }, [speakNextInQueue]);
  useEffect(() => { onAvatarSegmentEndRef.current = onAvatarSegmentEnd; }, [onAvatarSegmentEnd]);
  useEffect(() => () => clearSpeechWatchdog(), [clearSpeechWatchdog]);

  /** Add a text to the avatar speech queue and start speaking if idle. */
  const enqueueAvatarSpeech = useCallback((text) => {
    const t = String(text ?? "").trim();
    if (!t) return;
    speechQueueRef.current.push(t);
    if (!segmentInFlightRef.current) {
      speakNextRef.current?.();
    }
  }, []);

  /* =========================
     🎤 STOP MIC + PROCESS AUDIO
  ========================= */
  const stopMic = useCallback(async ({ skipProcessing = false } = {}) => {
    try {
      micEnabledRef.current = false;
      const buffers    = [...recordedBuffersRef.current];
      recordedBuffersRef.current = [];
      const sampleRate = audioContextRef.current?.sampleRate || 44100;

      try { processorRef.current?.disconnect(); sourceRef.current?.disconnect(); } catch (_) {}
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (audioContextRef.current) await audioContextRef.current.close().catch(() => {});
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
      processorRef.current = sourceRef.current = analyserRef.current = audioContextRef.current = streamRef.current = null;
      setMicEnabled(false);

      if (skipProcessing) { setIsLoading(false); return; }

      // User closed mic before speaking — skip STT and leave mic closed.
      // User must manually reopen the mic to start another turn.
      if (!hasSpokenRef.current) {
        console.debug("[Avatar] mic closed with no speech — skipping STT, mic stays off");
        setIsLoading(false);
        setIsAvatarSpeaking(false);
        isAvatarSpeakingRef.current = false;
        return;
      }

      if (buffers.length > 0) {
        let turnUserTextValid = false;
        try {
          setIsLoading(true);
          const wavBlob = convertBuffersToWAV(buffers, sampleRate);

          const userProfile = getUserProfile();
          const customerId  = userProfile?.customer_id;

          // ── Eager avatar speech ─────────────────────────────────────────────
          // Every llm_response streamed over the WebSocket is enqueued for the
          // avatar to speak IMMEDIATELY — while the backend is still running
          // tools / loading products — instead of waiting for the full turn.
          turnActiveRef.current = true;
          const llmParts = [];
          const enqueuedSegments = [];
          let transcriptOk = false;

          const flushSegmentsToSpeech = () => {
            // Only speak once the server confirmed a valid transcription.
            if (!transcriptOk) return;
            const segments = splitAssistantPartsForDisplay(llmParts);
            for (let i = 0; i < segments.length; i++) {
              const seg = segments[i];
              if (i < enqueuedSegments.length) {
                // A streaming frame extended an earlier segment — update it in
                // the queue only if it hasn't been sent to the avatar yet.
                if (seg !== enqueuedSegments[i]) {
                  const qIdx = speechQueueRef.current.indexOf(enqueuedSegments[i]);
                  if (qIdx >= 0) speechQueueRef.current[qIdx] = seg;
                  enqueuedSegments[i] = seg;
                }
              } else {
                enqueuedSegments.push(seg);
                enqueueAvatarSpeech(seg);
              }
            }
          };

          let lastResponse = null;
          const ChartvertextResponse = await OrbieeFlow(
            { audioBlob: wavBlob },
            customerId,
            navigate,
            addToCart,
            "eng",
            (lr) => {
              if (typeof cartSyncRef.current === "function") {
                cartSyncRef.current(lr);
              }
              if (typeof wishlistSyncRef.current === "function") {
                wishlistSyncRef.current(lr);
              }
              lastResponse = lr;
            },
            {
              mode: "avatar",
              onTranscript: () => {
                transcriptOk = true;
                flushSegmentsToSpeech();
              },
              onLlmResponse: (text) => {
                if (typeof text === "string" && text.trim()) {
                  llmParts.push(text);
                }
                flushSegmentsToSpeech();
              },
            }
          );

          // Backend turn finished — from here on, once the queue drains the
          // mic can be handed back to the user.
          turnActiveRef.current = false;

          const userText =
            lastResponse?.user_transcription ||
            lastResponse?.transcription ||
            "";
          turnUserTextValid = isValidTranscription(userText);

          if (turnUserTextValid) {
            const botText =
              ChartvertextResponse ||
              "Something went wrong, please try again.";
            const botTexts = lastResponse
              ? buildChatBubbleTextsFromResponse(lastResponse)
              : [botText];

            const intentRaw = lastResponse?.intent;
            const intents = Array.isArray(intentRaw)
              ? intentRaw.map(String)
              : intentRaw != null && intentRaw !== ""
                ? [String(intentRaw)]
                : [];
            if (intents.some((i) => i.includes("virtual_try_on"))) {
              setVirtualTryOnHoldAvatarMic(true);
            }

            window.dispatchEvent(
              new CustomEvent("orbieeBackgroundChat", {
                detail: {
                  userText,
                  botText,
                  botTexts: botTexts.length ? botTexts : [botText],
                },
              })
            );

            if (botText) {
              if (enqueuedSegments.length === 0) {
                // No llm_response frames were streamed (fallback backends) —
                // speak the final merged text like before.
                enqueueAvatarSpeech(botText);
              } else {
                console.log("botText", botText);
              }
            }

            setIsLoading(false);

            // If the avatar already finished speaking every queued response (or
            // nothing was queued), close out the loop now — otherwise
            // onAvatarSegmentEnd does it after the last response finishes.
            if (!segmentInFlightRef.current && speechQueueRef.current.length === 0) {
              await finishAvatarPlayback();
            }
          } else {
            // STT returned nothing useful — clear any premature speech and reopen mic
            turnActiveRef.current = false;
            speechQueueRef.current = [];
            setIsLoading(false);
            setIsAvatarSpeaking(false);
            isAvatarSpeakingRef.current = false;
            tryEnableMicAfterAvatarAndVtoRef.current?.();
          }
        } catch (error) {
          turnActiveRef.current = false;
          speechQueueRef.current = [];
          if (isEmptyTranscriptError(error)) {
            console.debug("[Avatar] empty transcript — reopening mic");
          } else {
            console.error("❌ Voice chat error:", error);
          }
          setIsLoading(false);
          setIsAvatarSpeaking(false);
          isAvatarSpeakingRef.current = false;
          tryEnableMicAfterAvatarAndVtoRef.current?.();
        }
      } else {
        console.log("⚠️ No audio buffers recorded");
        setIsLoading(false);
        tryEnableMicAfterAvatarAndVtoRef.current?.();
      }
    } catch (err) {
      console.error("Stop mic error:", err);
    }
  }, [navigate, addToCart, enqueueAvatarSpeech, finishAvatarPlayback]);

  useEffect(() => {
    tryEnableMicAfterAvatarAndVtoRef.current = tryEnableMicAfterAvatarAndVto;
  }, [tryEnableMicAfterAvatarAndVto]);

  /* =========================
     🎤 ENABLE MIC PROGRAMMATICALLY
  ========================= */
  const enableMicProgrammatically = useCallback(async () => {
    if (conversationStoppedRef.current || micEnabledRef.current) return;
    if (isVirtualTryOnHoldingAvatarMic()) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;
      const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
      processorRef.current = processor;
      recordedBuffersRef.current = [];
      processor.onaudioprocess = (e) => {
        if (!micEnabledRef.current) return;
        recordedBuffersRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = ANALYSER_FFT_SIZE;
      analyser.smoothingTimeConstant = 0;
      analyserRef.current = analyser;
      source.connect(processor);
      source.connect(analyser);
      processor.connect(audioContext.destination);
      hasSpokenRef.current = false;
      micEnabledRef.current = true;
      setMicEnabled(true);
      setIsLoading(false);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const checkSilence = () => {
        if (!micEnabledRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const volume = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
        if (volume > SPEECH_THRESHOLD_START_MIC) {
          hasSpokenRef.current = true;
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        } else if (volume < SILENCE_THRESHOLD_START_MIC && hasSpokenRef.current) {
          if (!silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => stopMic(), SILENCE_DURATION_MS);
          }
        }
        requestAnimationFrame(checkSilence);
      };
      checkSilence();
    } catch (err) {
      console.error("Mic access denied:", err);
    }
  }, [stopMic]);

  useEffect(() => {
    enableMicProgrammaticallyRef.current = enableMicProgrammatically;
  }, [enableMicProgrammatically]);

  useEffect(() => {
    const onVtoMicReleased = () => {
      tryEnableMicAfterAvatarAndVto();
    };
    window.addEventListener(
      "orbieeVirtualTryOnAvatarMicReleased",
      onVtoMicReleased
    );
    return () =>
      window.removeEventListener(
        "orbieeVirtualTryOnAvatarMicReleased",
        onVtoMicReleased
      );
  }, [tryEnableMicAfterAvatarAndVto]);

  /* =========================
     🎤 MIC TOGGLE
  ========================= */
  const toggleMic = async (e) => {
    e.stopPropagation();
    if (isLoading || isAvatarVideoLoading || isAvatarSpeaking) return;

    if (!micEnabledRef.current) {
      // Manual mic tap restarts the conversation loop after a stop.
      conversationStoppedRef.current = false;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = audioContext;
        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        const processor = audioContext.createScriptProcessor(SCRIPT_PROCESSOR_BUFFER_SIZE, 1, 1);
        processorRef.current = processor;
        recordedBuffersRef.current = [];
        processor.onaudioprocess = (ev) => {
          if (!micEnabledRef.current) return;
          recordedBuffersRef.current.push(new Float32Array(ev.inputBuffer.getChannelData(0)));
        };
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = ANALYSER_FFT_SIZE;
        analyser.smoothingTimeConstant = 0;
        analyserRef.current = analyser;
        source.connect(processor);
        source.connect(analyser);
        processor.connect(audioContext.destination);
        hasSpokenRef.current = false;
        micEnabledRef.current = true;
        setMicEnabled(true);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const checkSilence = () => {
          if (!micEnabledRef.current) return;
          analyser.getByteFrequencyData(dataArray);
          const volume = dataArray.reduce((s, v) => s + v, 0) / dataArray.length;
          if (volume > SPEECH_THRESHOLD_TOGGLE_MIC) {
            hasSpokenRef.current = true;
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          } else if (volume < SILENCE_THRESHOLD_TOGGLE_MIC && hasSpokenRef.current) {
            if (!silenceTimeoutRef.current) {
              silenceTimeoutRef.current = setTimeout(() => stopMic(), SILENCE_DURATION_MS);
            }
          }
          requestAnimationFrame(checkSilence);
        };
        checkSilence();
      } catch (err) {
        console.error("Mic access denied:", err);
      }
    } else {
      stopMic();
    }
  };

  /**
   * End the avatar conversation loop. If the mic is recording, discard that
   * audio (do not send it). Automatic mic reopen stays off until the user
   * taps the mic again.
   */
  const stopAvatarConversation = useCallback(async (e) => {
    e.stopPropagation();
    conversationStoppedRef.current = true;
    if (micEnabledRef.current) {
      await stopMic({ skipProcessing: true });
    }
  }, [stopMic]);

  /* =========================
     📡 STREAM START — WebRTC begins sending frames
  ========================= */
  useEffect(() => {
    const handleStreamStart = (event) => {
      cancelPendingStreamDone();
      setIsLoading(false);

      if (event.detail?.stream) {
        webrtcStreamRef.current = event.detail.stream;
        const video = mainVideoRef.current;
        if (video) {
          // Attach the WebRTC stream to the video element
          if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
          video.src   = ""; // clear any idle loop src
          video.loop  = false;
          video.srcObject = event.detail.stream;
          video.muted     = !audioEnabled;
          video.volume    = 1;
          video.play().catch((err) => {
            if (err.name !== "AbortError") console.warn("WebRTC stream play error:", err);
          });
        }
      }

      setIsAvatarSpeaking(true);
      videoLoadedRef.current = true;
      setAvatarLoadingState(false);
    };

    window.addEventListener("avatarStreamStart", handleStreamStart);
    return () => window.removeEventListener("avatarStreamStart", handleStreamStart);
  }, [audioEnabled, setAvatarLoadingState, cancelPendingStreamDone]);

  /* =========================
     📡 STREAM DONE — avatar finished one response → next in queue or idle loop
  ========================= */
  useEffect(() => {
    const handleStreamDone = () => {
      // stream/done can fire twice (server + client data channels) — schedule once.
      if (streamDoneTimerRef.current) return;
      clearSpeechWatchdog();

      const video = mainVideoRef.current;
      const isWebRtcPlayback = !!(video?.srcObject);
      const tailMs = isWebRtcPlayback ? STREAM_DONE_PLAYBACK_TAIL_MS : 0;

      streamDoneTimerRef.current = setTimeout(() => {
        streamDoneTimerRef.current = null;
        onAvatarSegmentEnd();
      }, tailMs);
    };

    window.addEventListener("avatarStreamDone", handleStreamDone);
    return () => {
      window.removeEventListener("avatarStreamDone", handleStreamDone);
      cancelPendingStreamDone();
    };
  }, [onAvatarSegmentEnd, cancelPendingStreamDone, clearSpeechWatchdog]);

  /* =========================
     🚀 START AVATAR (after login)
  ========================= */
  useEffect(() => {
    if (!user || loading) return;
    if (sessionStorage.getItem("CallAvatarVideo") === "false") return;
    if (avatarWelcomeInFlightRef.current) return;

    avatarWelcomeInFlightRef.current = true;

    const startAvatar = async () => {
      try {
        setIsLoading(true);
        videoLoadedRef.current = false;
        setAvatarLoadingState(true);

        // Wait for backend welcome in parallel with WebRTC; fall back if none arrives.
        const welcomePromise = waitForOrbieeBackendWelcome(2500);

        // 1. Establish WebRTC connection (stream will come via avatarStreamStart event)
        await ensureAvatarConnected();
        sessionStorage.setItem("CallAvatarVideo", "false");

        const backendWelcome =
          (await welcomePromise) || getOrbieeBackendWelcomeMessage();
        const message =
          (backendWelcome && String(backendWelcome).trim()) ||
          resolveWelcomeMessage(user.customer_name);

        // 2. Mark avatar as speaking and send the welcome text
        cancelPendingStreamDone();
        setIsAvatarSpeaking(true);
        await sendText(message);
        setAudioEnabled(true);

        // Fallback: if avatarStreamStart never fires, clear loading after 5 s
        setTimeout(() => {
          if (!videoLoadedRef.current) setAvatarLoadingState(false);
        }, 5000);
      } catch (err) {
        console.error("❌ Avatar init error:", err);
        setIsLoading(false);
        setIsAvatarSpeaking(false);
        setAvatarLoadingState(false);
        avatarWelcomeInFlightRef.current = false;
      }
    };

    startAvatar();
  }, [user, loading, ensureAvatarConnected, setAvatarLoadingState, cancelPendingStreamDone, resolveWelcomeMessage]);

  useEffect(() => {
    if (!user) {
      avatarWelcomeInFlightRef.current = false;
    }
  }, [user]);

  /* =========================
     🎞 LOADING VIDEO RAF
  ========================= */
  useEffect(() => {
    const video = loadingVideoRef.current;
    if (!video || !isLoading) return;
    const onPlay  = () => { cancelAnimationFrame(loadingRafRef.current); processLoadingFrame(); };
    const onPause = () => cancelAnimationFrame(loadingRafRef.current);
    video.addEventListener("play",  onPlay);
    video.addEventListener("pause", onPause);
    return () => {
      video.removeEventListener("play",  onPlay);
      video.removeEventListener("pause", onPause);
      cancelAnimationFrame(loadingRafRef.current);
    };
  }, [isLoading, processLoadingFrame]);

  // Mic is disabled while loader is active, avatar is loading, or avatar is speaking
  const isMicDisabled = isLoading || isAvatarVideoLoading || isAvatarSpeaking;

  /* =========================
     🖼 RENDER
  ========================= */
  return (
    <Box
     onClick={enableAudio}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: "12px",
        transition: "box-shadow 0.3s ease",
        animation: isLoading && welcomeDoneRef.current
          ? "avatarBoxGlow 1.8s ease-in-out infinite"
          : "none",
        "@keyframes avatarBoxGlow": {
          "0%, 100%": {
            boxShadow:
              "inset 0 0 12px 3px rgba(58, 105, 140, 0.5), inset 0 0 24px 6px rgba(93, 168, 224, 0.2)",
          },
          "50%": {
            boxShadow:
              "inset 0 0 20px 8px rgba(58, 105, 140, 0.9), inset 0 0 40px 16px rgba(93, 168, 224, 0.4), inset 0 0 60px 24px rgba(100, 180, 255, 0.15)",
          },
        },
      }}
    >
      {/* ── AUDIO / INTRO VIDEO (before login) ───────────────────────────── */}
      {step === "audio" && !user && (
        <>
          <video
            ref={audioVideoRef}
            src={AudioVideo}
            crossOrigin="anonymous"
            muted
            playsInline
            preload="auto"
            onEnded={() => setStep("idle")}
            onLoadedData={() => {
              const video  = audioVideoRef.current;
              const canvas = audioCanvasRef.current;
              if (video && !audioEnabled) video.pause();
              if (video && canvas && video.readyState >= 2) {
                video.currentTime = 0;
                setTimeout(() => { if (video && canvas && video.readyState >= 2) applyChromaKey(video, canvas); }, 100);
              }
            }}
            style={{ display: "none" }}
          />
          <canvas ref={audioCanvasRef} style={AVATAR_CANVAS_DISPLAY_STYLE} />
        </>
      )}

      {/* ── IDLE / AVATAR VIDEO (after login)
            mainVideoRef is ALWAYS in the DOM when step === "idle".
            In idle mode   → src = IdleVideo, loop = true
            In avatar mode → srcObject = WebRTC stream (set by connectAvatar's ontrack handler)
            Having a single persistent element means the WebRTC srcObject is never lost.
      ──────────────────────────────────────────────────────────────────────── */}
      {step === "idle" && (
        <>
          <video
            ref={mainVideoRef}
            crossOrigin="anonymous"
            autoPlay
            muted={!audioEnabled}
            playsInline
            preload="auto"
            onLoadedData={async () => {
              const video = mainVideoRef.current;
              if (!video) return;
              if (playPromiseRef.current) { playPromiseRef.current.catch(() => {}); playPromiseRef.current = null; }
              try {
                video.muted = !audioEnabled;
                playPromiseRef.current = video.play();
                await playPromiseRef.current;
                playPromiseRef.current = null;
              } catch (err) {
                playPromiseRef.current = null;
                if (err.name !== "AbortError") console.warn("mainVideoRef onLoadedData play error:", err);
              }
              if (!videoLoadedRef.current) { videoLoadedRef.current = true; setAvatarLoadingState(false); }
            }}
            onEnded={() => {
              // Video-file mode: clip ended → return to idle loop
              if (!mainVideoRef.current?.loop) {
                setIsAvatarSpeaking(false);
                window.dispatchEvent(new CustomEvent("avatarStreamDone"));
              }
            }}
            style={{ display: "none" }}
          />
          <canvas ref={mainCanvasRef} style={AVATAR_CANVAS_DISPLAY_STYLE} />
        </>
      )}

      {/* ── STOP CONVERSATION LOOP ───────────────────────────────────────── */}
      {user && (
        <IconButton
          onClick={stopAvatarConversation}
          aria-label="Stop avatar recording"
          title="Stop avatar recording"
          sx={{
            position: "absolute", bottom: 8, right: 16,
            backgroundColor: "rgba(0,0,0,0.6)",
            color: "#fff", zIndex: 20,
            "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
          }}
        >
          <StopCircleOutlinedIcon />
        </IconButton>
      )}

      {/* ── MIC ──────────────────────────────────────────────────────────── */}
      <IconButton
        onClick={toggleMic}
        disabled={isMicDisabled}
        aria-label={micEnabled ? "Mute microphone" : "Unmute microphone"}
        title={micEnabled ? "Mute microphone" : "Unmute microphone"}
        sx={{
          position: "absolute", bottom: 8, right: 96,
          backgroundColor: micEnabled ? "rgba(255,0,0,0.7)" : "rgba(0,0,0,0.6)",
          color: "#fff", zIndex: 20,
          opacity: isMicDisabled ? 0.5 : 1,
          cursor: isMicDisabled ? "not-allowed" : "pointer",
          "&:hover": {
            backgroundColor: isMicDisabled
              ? (micEnabled ? "rgba(255,0,0,0.7)" : "rgba(0,0,0,0.6)")
              : (micEnabled ? "rgba(255,0,0,0.9)" : "rgba(0,0,0,0.85)"),
          },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        {micEnabled ? <MicIcon /> : <MicOffIcon />}
      </IconButton>

      {/* ── SPEAKER ──────────────────────────────────────────────────────── */}
      <IconButton
        onClick={toggleAudio}
        aria-label={audioEnabled ? "Mute avatar" : "Unmute avatar"}
        title={audioEnabled ? "Mute avatar" : "Unmute avatar"}
        sx={{
          position: "absolute", bottom: 8, right: 56,
          backgroundColor: "rgba(0,0,0,0.6)", color: "#fff", zIndex: 20,
          "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
        }}
      >
        {audioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
      </IconButton>

      {/* ── LOADER OVERLAY ───────────────────────────────────────────────── */}
     
    </Box>
  );
}
